<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useClipboard } from '@/composables/clipboard'
import { useColumns } from '@/composables/order/supplier-order-row-grid'
import { useDialogTitle } from '@/composables/order/supplier-order-grid'
import { useDocumentRow } from '@/composables/document-row'

import { useProductService } from '@/services/product.service'

import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useOrderStore } from '@/stores/order.store'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import ClipboardExportDialog from '@/components/shared/ClipboardExportDialog.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import DocumentRowTypeDropDownBox from '@/components/shared/DocumentRowTypeDropDownBox.vue'
import DocumentRowTypeToolbar from '@/components/shared/DocumentRowTypeToolbar.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import ProductDropDownBox from '@/components/shared/ProductDropDownBox.vue'
import SupersessionProductDropDownBox from '@/components/shared/SupersessionProductDropDownBox.vue'
import TimeEquivalentTextBox from '@/components/shared/TimeEquivalentTextBox.vue'
import ImportRowIcon from '@/components/shared/ImportRowIcon.vue'
import { usePortalStore } from '@/stores/portal.store'

const props = defineProps({
  data: Object,
  entityLock: Object,
  readOnly: {
    type: Boolean,
    default: false
  },
})

const webSiteStore = useWebSiteStore()
const { gridRef, refreshGridAsync, focusGrid, setCellValueByField } = dataGrid.useGridRef()
const { documentRowType, selectedRows, batchDeleteSelectedRows, getEditMainInfoColumns, onDocumentRowUpdated, onDocumentRowInitNewRow, onDocumentRowEditorPreparing, onDocumentRowEditorPreparingSetIsReadOnly, onDocumentRowTypeChanged, onDocumentRowSelectionChanged, focusQuantityEditor } = useDocumentRow(gridRef)

const emits = defineEmits(['row-saved'])

const urgencyTypeId = props.data?.urgency_type_ref
const discount = props.data?.discount

const saveStateId = `supplier-order-row-grid`
const gridId = `${saveStateId}_${props.data?.id || 0}`

const i18n = getI18n()
const registryStore = useRegistryStore()
const orderStore = useOrderStore()
const portalStore = usePortalStore()
const productService = useProductService()

const isToolbarDisabled = ref(true)
const supersessionId = ref(null)
const productUnitOfMeasure = ref(null)
const includesVat = ref(false)
const currentCustomer = computed(() => registryStore.customers.find(i => i.id === props.data?.customer_id))
const keepDescription = ref(true)
const allowedRowTypes = [webSiteStore.documentRowTypesEnum.PRODUCT, webSiteStore.documentRowTypesEnum.TEXT]

const computedDataSource = computed(() => {
  const supplierOrder = props.data?.id
  return dataGrid.useODataSource(orderStore.api.getAbsoluteUrl() + orderStore.api.supplierOrderRowsUrl, { customParamsFn: () => { return { supplier_order: supplierOrder } } })
})

const formInstance = ref(null)
const productDropDownRef = ref(null)

const clipboard = useClipboard()

const editors = ref({})
let orderSupplierBrand = registryStore.supplierBrands.find(i => i.id === props.data?.supplier_brand_ref)
const supplier = ref(registryStore.suppliers.find(i => i.id === props.data?.supplier))
const supplierDivision = ref(registryStore.supplierDivisions.find(i => i.id === props.data?.supplier_division))
if (supplierDivision.value) {
  if (supplierDivision.value.brand_behavior === registryStore.supplierDivisionBrandBehavior.SUP_DIV) {
    orderSupplierBrand = registryStore.supplierBrands.find(i => i.id === supplierDivision.value.supplier_brand_ref)
  }
  if (supplierDivision.value.brand_behavior === registryStore.supplierDivisionBrandBehavior.DIV_SUP) {
    orderSupplierBrand = registryStore.supplierBrands.find(i => i.id === supplier.value.supplier_brand_ref)
  }
}
const supplierBrand = ref(orderSupplierBrand)

const currency = webSiteStore.currencies.find(i => i.id === props.data?.currency_ref)

const columns = useColumns(supplierBrand, keepDescription, currency, documentRowType, supersessionId, includesVat, productUnitOfMeasure, currentCustomer)

const rowButtons = props.entityLock.getEntityLockButtons()

const { defaultActionTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.supplierOrderRow')}`
  if (i.id) {
    title += ` ${i.row_number}`
  }
  return `${title} | ${useDialogTitle().itemTitle(props.data)}`
}

const onInitialized = (e) => {
  formInstance.value = e.component
}

const getEditColumns = (documentRowType) => {
  const mainInfoDataFields = getEditMainInfoColumns(documentRowType, dataGrid)
  const hideTabs = [webSiteStore.documentRowTypesEnum.TEXT, webSiteStore.documentRowTypesEnum.FREE].includes(documentRowType)

  return [
    {
      itemType: 'group',
      colSpan: 2,
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.mainInfo'),
          name: 'mdi-view-list',
          captionTemplate: 'row-type-caption-template',
          colCount: 12,
          items: mainInfoDataFields,
        },
        documentRowType !== webSiteStore.documentRowTypesEnum.TEXT ? {
          itemType: 'group',
          caption: i18n.t('common.additionalInfo'),
          name: 'mdi-currency-usd-circle',
          captionTemplate: 'generic-caption-template',
          cssClass: 'my-form-wrapper',
          colCount: 4,
          items: [
            { dataField: 'product_cost', editorOptions: { readOnly: true, ...dataGrid.useCurrencyOptions({ currency: currency }) }, label: { text: i18n.t('common.cost') } },
            { dataField: 'cost_valorization' },
            { dataField: 'discount_category_ref', colSpan: 2, editorOptions: { readOnly: true } },
            { dataField: 'product_price', editorOptions: dataGrid.useCurrencyOptions({currency: currency, readOnly: true}) },
            { dataField: 'charge_percentage', editorType: 'dxNumberBox', editorOptions: dataGrid.usePercentageOptions()},
            { dataField: 'price', label: { text: i18n.t('common.unitPriceNoVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, precision: registryStore.companyData.default_display_price_precision}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
            { dataField: 'price_vat', label: { text: i18n.t('common.unitPriceVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, precision: registryStore.companyData.default_display_price_precision}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
            { dataField: 'discount', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
            { dataField: 'discount_2', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
            { dataField: 'discount_3', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
            { dataField: 'total_discount', editorOptions: {...dataGrid.usePercentageOptions(), readOnly: true }, label: { text: i18n.t('common.totalDiscount') } },
            { dataField: 'vat_type_ref', editorOptions: { showClearButton: true }, colSpan: 2 },
            { dataField: 'total_no_vat', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, readOnly: true}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
            { dataField: 'total_vat', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, readOnly: true}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
          ]
        } : { itemType: 'empty' }
      ]
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      name: 'tabbed',
      tabs: hideTabs ? [] : [
        {
          title: i18n.t('common.stock'),
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.stock'),
              colCount: 2,
              items: [{ dataField: 'deposit' }, { dataField: 'garage' }]
            },
          ]
        },
        {
          title: i18n.t('common.ties'),
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.ties'),
              items: [{ template: 'supersessionProductEditor', colSpan: 5 }]},
          ]
        },
        {
          title: i18n.t('common.campaigns'),
          items: [{ itemType: 'group', caption: i18n.t('common.campaigns')}]
        },
        {
          title: i18n.t('common.statistics'),
          items: [{ itemType: 'group', caption: i18n.t('common.statistics') }]
        },
        {
          title: i18n.t('common.operatorsStock'),
          items: [{itemType: 'group', caption: i18n.t('common.operatorsStock')}]
        },
      ]
    },
  ]
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 5,
  items: getEditColumns(documentRowType.value),
  onInitialized: onInitialized,
}, {
  isSaveAndNew: true,
  onInitializedCustom: (e) => props.entityLock?.readOnlyToolbarItems(e),
  entityLock: props.entityLock,
})

const onInitNewRow = async(e) => {
  supersessionId.value = productUnitOfMeasure.value = null
  e.data.supplier_order = props.data?.id
  e.data.urgency_type_ref = urgencyTypeId

  const brand = registryStore.supplierBrands.find(i => i.id === props.data?.supplier_brand_ref) ??
   registryStore.supplierBrands.find(i => i.id === registryStore.companyData.default_supplier_brand_ref)

  supplierBrand.value = (dataGrid.isSaveAndNew.value ? supplierBrand.value : null) ?? brand

  productService.updateTotalDiscountValue(e.data, e.data)

  onDocumentRowInitNewRow(e, supplierBrand, discount, isToolbarDisabled, props.data?.deposit)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)

  setTimeout(() => productDropDownRef.value?.focus?.(), 1000)
}

const onEditorPreparing = (e) => {
  onDocumentRowEditorPreparing(e, isToolbarDisabled, supplierBrand, includesVat)

  if (e.parentType === 'dataRow') {
    e.editorOptions.onInitialized = (arg) => { editors.value[e.dataField] = arg.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'product_ref') {
    e.editorOptions.disabled = !!e.row.data?.id || (typeof e.row.data?.supplier_brand_ref !== 'number')
  }
  if (e.parentType === 'dataRow' && e.dataField === 'product_price') {
    e.editorOptions.readOnly = true
  }
  if (e.parentType === 'dataRow' && e.dataField === 'supplier_brand_ref') {
    supplierBrand.value = registryStore.getSupplierBrandById(e.row.data?.supplier_brand_ref)

    if (orderSupplierBrand && orderSupplierBrand.has_brand_check) {
      e.editorOptions.readOnly = true
    } else {
      const defaultValueChangeHandler = e.editorOptions.onValueChanged
      e.editorOptions.onValueChanged = (args) => {
        supplierBrand.value = registryStore.getSupplierBrandById(args.value)
        defaultValueChangeHandler(args)
      }
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'document_row_type') {
    if (editors.value['supplier_brand_ref']) {
      if (documentRowType.value === webSiteStore.documentRowTypesEnum.PRODUCT) {
        supplierBrand.value = orderSupplierBrand
        editors.value['supplier_brand_ref'].option('value', orderSupplierBrand.id)
        if (supplierBrand.value && supplierBrand.value.has_brand_check) {
          editors.value['supplier_brand_ref'].option('readOnly', true)
        }
      }
    }
  }

  onDocumentRowEditorPreparingSetIsReadOnly(e, props.readOnly, editors)
}

const onDocumentRowTypeSelect = (rowType) => {
  documentRowType.value = rowType
  supplierBrand.value = null
  setCellValueByField('document_row_type', rowType)
  onDocumentRowTypeChanged(rowType, getEditColumns)
}

const onEditingStart = (e) => {
  supersessionId.value = e.data?.product_ref || null
  productUnitOfMeasure.value = e.data?.unit_of_measure_ref || null

  documentRowType.value = e.data?.document_row_type

  productService.updatePriceVatValue(e.data, e.data)
  productService.updateTotalDiscountValue(e.data, e.data)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
}

const onSaved = () => { emits('row-saved', props.data?.id) }

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    { location: 'after', template: 'dropdownToolbarItem' },
    clipboard.clipboardExportToolbarWidget({ disabled: props.readOnly || selectedRows.value.length === 0 })
  )

  dataGrid.useDefaultToolbar(e, {
    allowBatchDelete: true
  })
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  onDocumentRowSelectionChanged(e)
  clipboard.clipboardExportToolbarButton.value.option('disabled', selectedRows.value.length === 0)
}

const onSupersessionSelected = () => {
  const tabPanel = formInstance.value?.getEditor('tabbed')
  const tiesTabIndex = tabPanel?.option('items')?.findIndex(tab => tab.title === i18n.t('common.ties'))
  if (tiesTabIndex >= 0) {
    tabPanel.option('selectedIndex', tiesTabIndex)
  }
}

const onProductValueChanged = (e) => {
  if (e.value) { focusQuantityEditor(editors) }
}

const onNewProductSelected = (e) => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  grid.cellValue(rowIndex, 'product_ref', e)
  supersessionId.value = e?.id || null
}

const parentId = computed(() => props.data?.id)
const { onReorder } = dataGrid.useDefaultRowReordering(orderStore.api.reorderSupplierOrderRowsAsync, parentId, gridRef, (e) => props.readOnly || props.entityLock.isLocked(false, e.entityId))

const refreshGridWithFocus = () => refreshGridAsync().then(focusGrid & emits('row-saved', props.data?.id))

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  entityLockId: () => props.data?.id,
  saveStateId: saveStateId,
  mode: 'row',
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onEditingStart: onEditingStart,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onRowUpdated: onDocumentRowUpdated,
  onRowInserted: onDocumentRowUpdated,
  onSaved: onSaved,
  focusGrid: focusGrid,
  rowDragging: {
    allowReordering: true,
    onReorder: onReorder,
  },
  disabledFocusOnContentReady: true,
  splittedGridDetail: true,
})

const exportGridColumns = useColumns(supplierBrand, currency).filter(i => i.dataField !== 'unit_of_measure_ref' && i.dataField !== 'discount_category' && i.dataField !== 'total_no_vat')
</script>
<template>
  <div class="row-grid-ctn">
    <clipboard-export-dialog
      v-if="clipboard.clipboardExportDialogVisible.value"
      v-model:visible="clipboard.clipboardExportDialogVisible.value"
      grid-id="supplier-order-row-export-grid"
      :selected-rows="selectedRows"
      :columns="exportGridColumns"
      :document-type="portalStore.documentTypesEnum.ORDER_SUPPLIER_ORDER"
      :source-includes-vat="includesVat"
      :entity-lock="props.entityLock"
      :is-read-only-row="props.readOnly"
      @updated="refreshGridWithFocus"
      @cancel="focusGrid"
    />
    <dx-data-grid
      ref="gridRef"
      :key="gridId"
      v-bind="gridOptions"
      :data-source="computedDataSource"
    >
      <template #dropdownToolbarItem>
        <import-row-icon
          :disabled="props.readOnly || props.entityLock.isLocked(false, props.data?.id)"
          :document-type="portalStore.documentTypesEnum.ORDER_SUPPLIER_ORDER"
          :document-id="props.data?.id"
          :entity-lock="props.entityLock"
          :options="{ allowedDocumentRowTypes: allowedRowTypes }"
          @updated="refreshGridWithFocus"
        />
      </template>
      <template #generic-caption-template="{ data: cellInfo }">
        <div class="dx-form-group-caption-wrapper">
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>
      <template #productEditor="{ data: cellInfo }">
        <product-drop-down-box
          ref="productDropDownRef"
          :brand="supplierBrand"
          :document-row-type="documentRowType"
          :data="cellInfo"
          :options="{ hideSupersession: true }"
          @supersession-selected="onSupersessionSelected"
          @value-changed="onProductValueChanged"
        />
      </template>
      <template #supersessionProductEditor>
        <supersession-product-drop-down-box
          :data="{ supersession_id: supersessionId }"
          @new-product-selected="onNewProductSelected"
        />
      </template>
      <template #depositEditor="{ data: cellInfo }">
        <deposit-drop-down-box
          :data="cellInfo"
          :disabled="props.readOnly || documentRowType === webSiteStore.documentRowTypesEnum.TEXT"
        />
      </template>
      <template #row-type-caption-template="{ data: cellInfo }">
        <document-row-type-toolbar
          :data="cellInfo"
          :disabled="props.readOnly || isToolbarDisabled"
          :document-row-type="documentRowType"
          :allowed-row-types="allowedRowTypes"
          @value-changed="(e) => onDocumentRowTypeSelect(e.value)"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'SupplierOrder'"
          :master-entity-id="props.data?.id"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :master-entity-id="props.data?.id"
          :grid="gridRef"
          :entity-lock="props.entityLock"
        />
      </template>
      <template #customToolbarDeleteButtonTemplate>
        <custom-toolbar-delete-button-template
          :master-entity-id="props.data?.id"
          :selected-rows-id="batchDeleteSelectedRows?.map(i => i.id) || []"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :on-delete="orderStore.api.batchDeleteSupplierOrderRowsAsync"
          :disabled="props.readOnly || batchDeleteSelectedRows?.length === 0"
        />
      </template>
      <template #documentRowType="{ data: cellInfo }">
        <document-row-type-drop-down-box
          :data="cellInfo"
          :value="cellInfo?.data?.document_row_type"
          :disabled="true"
        />
      </template>
      <template #timeEquivalentEditor>
        <time-equivalent-text-box
          :selected-brand-id="editors['supplier_brand_ref'].option('value')"
          :selected-quantity="editors['quantity'].option('value')"
          :selected-uom-id="productUnitOfMeasure"
        />
      </template>
      <template #documentRowTypeCellTemplate="{ data: templateOptions }">
        <chip-status :data="webSiteStore.getLocalizedDocumentRowTypes().find(x => x.id === templateOptions.data.document_row_type)" />
      </template>
    </dx-data-grid>
  </div>
</template>

<style scoped lang="scss">
.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
